import React from 'react';
import { TableHead, TableRow, TableCell, Typography } from '@mui/material';
import { NumericFormat } from 'react-number-format';
import MultipleProgressBar from '../../../CustomComponents/MultipleProgressBar';
import { theme } from '../../../../theme';
import { Box } from '@mui/system';
import ProgressTooltip from './ProgressTooltip';


function PhysicianFooter(props) {
    const totalOpportunityValue = props.visiblePhysicians.reduce((acc, physician) => acc + physician.recommendation_summary.original_opportunity, 0);
    const totalTotalValue = props.visiblePhysicians.reduce((acc, physician) => acc + physician.recommendation_summary.total_value, 0);
    const totalNumRecs = props.visiblePhysicians.reduce((acc, physician) => acc + physician.recommendation_summary.total_recs, 0);
    const totalAcceptedValue = props.visiblePhysicians.reduce((acc, physician) => acc + physician.recommendation_summary.accepted_value, 0);
    const totalAcceptedPercentage = Math.round((totalAcceptedValue / Math.max(totalOpportunityValue, 1)) * 100);
    const totalNumAccepted = props.visiblePhysicians.reduce((acc, physician) => acc + physician.recommendation_summary.num_accepted, 0);
    const totalCustomValue = props.visiblePhysicians.reduce((acc, physician) => acc + physician.recommendation_summary.custom_value, 0);
    const totalCustomPercentage = Math.round((totalCustomValue / Math.max(totalOpportunityValue, 1)) * 100);
    const totalNumCustom = props.visiblePhysicians.reduce((acc, physician) => acc + physician.recommendation_summary.num_custom, 0);
    const totalApprovedValue = totalAcceptedValue + totalCustomValue;
    const totalApprovedPercentage = Math.round((Math.ceil(totalAcceptedValue + totalCustomValue) / Math.max(totalOpportunityValue, 1)) * 100);
    const totalNumApproved = totalNumAccepted + totalNumCustom;
    const countApprovedPercentage = Math.round((totalNumApproved / Math.max(totalNumRecs, 1)) * 100);
    const totalDeniedValue = props.visiblePhysicians.reduce((acc, physician) => acc + physician.recommendation_summary.rejected_value, 0);
    const totalDeniedPercentage = Math.round((totalDeniedValue / Math.max(totalOpportunityValue, 1)) * 100);
    const totalNumDenied = props.visiblePhysicians.reduce((acc, physician) => acc + physician.recommendation_summary.num_rejected, 0);
    const countDeniedPercentage = Math.round((totalNumDenied / Math.max(totalNumRecs, 1)) * 100);
    const totalUnreviewedValue = props.visiblePhysicians.reduce((acc, physician) => acc + physician.recommendation_summary.open_value, 0);
    const totalUnreviewedPercentage = Math.round((totalUnreviewedValue / Math.max(totalOpportunityValue, 1)) * 100);
    const totalNumUnreviewed = props.visiblePhysicians.reduce((acc, physician) => acc + physician.recommendation_summary.num_open, 0);
    const countUnreviewedPercentage = Math.round((totalNumUnreviewed / Math.max(totalNumRecs, 1)) * 100);
    const totalOmittedValue = props.visiblePhysicians.reduce((acc, physician) => acc + physician.recommendation_summary.omitted_value, 0);
    const totalOmittedPercentage = Math.round((totalOmittedValue / Math.max(totalOpportunityValue, 1)) * 100);
    const totalNumOmitted = props.visiblePhysicians.reduce((acc, physician) => acc + physician.recommendation_summary.num_omitted, 0);
    const countOmittedPercentage = Math.round((totalNumOmitted / Math.max(totalNumRecs, 1)) * 100);

    // total reviewed values, rounded to nearest thousand
    const totalReviewedValueK = Math.round((totalTotalValue - totalUnreviewedValue) / 100 / 1000);
    const totalTotalValueK = Math.round(totalTotalValue / 100 / 1000);
    const totalReviewedValuePercent = Math.round((Math.ceil(totalAcceptedValue + totalCustomValue + totalDeniedValue) / Math.max(totalTotalValue, 1)) * 100);
    const totalReviewedCount = totalNumRecs - totalNumUnreviewed;
    const totalReviewedCountPercent = Math.round((totalReviewedCount / Math.max(totalNumRecs, 1)) * 100);

    return (
        <TableHead sx={{ bottom: 0, left: 0, position: 'sticky', zIndex: 2 }}>
            <TableRow className='tableFooter' sx={{ textOverflow: 'ellipsis', overflow: 'hidden', color: 'black', height: '60px' }}>
                <TableCell>
                    <Typography variant='heavy' fontSize='12px'>
                        <strong>{props.totalPhysicians} Total</strong>
                    </Typography>
                </TableCell>
                <TableCell>
                    <ProgressTooltip 
                        acceptedCount={totalNumAccepted} 
                        acceptedValue={totalAcceptedValue / 100} 
                        customCount={totalNumCustom}
                        customValue={totalCustomValue / 100}
                        deniedCount={totalNumDenied} 
                        deniedValue={totalDeniedValue / 100} 
                        unreviewedCount={totalNumUnreviewed} 
                        unreviewedValue={totalUnreviewedValue / 100}
                    >
                        <Box>
                            <MultipleProgressBar segments={[
                                {
                                    value: totalAcceptedPercentage,
                                    color: `${theme.palette.blue.main}`
                                },
                                {
                                    value: totalCustomPercentage,
                                    color: `${theme.palette.background.customProgress}`
                                },
                                {
                                    value: totalDeniedPercentage,
                                    color: `${theme.palette.background.deniedProgress}`
                                },
                                {
                                    value: totalUnreviewedPercentage,
                                    color: `${theme.palette.background.unreviewedProgress}`
                                }
                            ]} label={props.numericFormat === 'currency' ? 
                                <strong>{`$${totalReviewedValueK}k / $${totalTotalValueK}k (${totalReviewedValuePercent}%)`}</strong> : 
                                <strong>{`${totalReviewedCount} / ${totalNumRecs} Optimizations (${totalReviewedCountPercent}%)`}</strong>
                            } />
                        </Box>
                    </ProgressTooltip>
                </TableCell>
                <TableCell>
                    <Typography variant='heavy' fontSize='12px'>
                        <strong>
                            { props.numericFormat === 'currency' &&
                                <NumericFormat value={totalOpportunityValue / 100} displayType={'text'} thousandSeparator={true} prefix={'$'} decimalScale={0} fixedDecimalScale />
                            }
                            { props.numericFormat === 'count' &&
                                `${totalNumRecs} Optimizations`
                            }
                        </strong>
                    </Typography>
                </TableCell>
                <TableCell>
                    <Typography variant='heavy' fontSize='12px'>
                        <strong>
                            { props.numericFormat === 'currency' &&
                                <NumericFormat value={totalApprovedValue / 100} displayType={'text'} thousandSeparator={true} prefix={'$'} suffix={` (${totalApprovedPercentage}%)`} decimalScale={0} fixedDecimalScale />
                            }
                            { props.numericFormat === 'count' &&
                                `${totalNumApproved} Optimizations (${countApprovedPercentage}%)`
                            }
                        </strong>
                    </Typography>
                </TableCell>
                <TableCell>
                    <Typography variant='heavy' fontSize='12px'>
                        <strong>
                            { props.numericFormat === 'currency' &&
                                <NumericFormat value={totalDeniedValue / 100} displayType={'text'} thousandSeparator={true} prefix={'$'} suffix={` (${totalDeniedPercentage}%)`} decimalScale={0} fixedDecimalScale />
                            }
                            { props.numericFormat === 'count' &&
                                `${totalNumDenied} Optimizations (${countDeniedPercentage}%)`
                            }
                        </strong>
                    </Typography>
                </TableCell>
                <TableCell>
                    <Typography variant='heavy' fontSize='12px'>
                        <strong>
                            { props.numericFormat === 'currency' &&
                                <NumericFormat value={totalUnreviewedValue / 100} displayType={'text'} thousandSeparator={true} prefix={'$'} suffix={` (${totalUnreviewedPercentage}%)`} decimalScale={0} fixedDecimalScale />
                            }
                            { props.numericFormat === 'count' &&
                                `${totalNumUnreviewed} Optimizations (${countUnreviewedPercentage}%)`
                            }
                        </strong>
                    </Typography>
                </TableCell>
                <TableCell>
                    <Typography variant='heavy' fontSize='12px'>
                        <strong>
                            { props.numericFormat === 'currency' &&
                                <NumericFormat value={totalOmittedValue / 100} displayType={'text'} thousandSeparator={true} prefix={'$'} suffix={` (${totalOmittedPercentage}%)`} decimalScale={0} fixedDecimalScale />
                            }
                            { props.numericFormat === 'count' &&
                                `${totalNumOmitted} Optimizations (${countOmittedPercentage}%)`
                            }
                        </strong>
                    </Typography>
                </TableCell>
            </TableRow>
        </TableHead>
    )
}

export default PhysicianFooter;
